import React, { useState, useEffect, useContext } from "react"
import {
  VStack, Box, Text, HStack, Badge,
} from "@chakra-ui/react"
import Modal from "components/dialogs/Modal"
import Button from "components/Buttons/Button"
import {
  BadgeTemplate,
} from "sharedTypes/badge"
import { errorToast, successToast } from "utilities/toasts"
import { getCapability, validateTemplateDimensions, calculatePrintInstructions } from "components/BadgeBuilder/utils/badgeHelpers"
import BadgeDimensionsForm from "components/BadgeBuilder/BadgeDimensionsForm/BadgeDimensionsForm"
import { BadgeUIContext } from "components/BadgeBuilder/contexts/BadgeUIContext"
import { formatMeasurement } from "components/BadgeBuilder/utils/units"
import DevOnly from "components/BadgeBuilder/DevOnly"

type Props = {
  isOpen: boolean
  onClose: () => void
  template: BadgeTemplate
  onTemplateChange: (
    updates: Partial<Pick<BadgeTemplate, "name" | "widthMm" | "heightMm" | "printerModel" | "printerMedia" | "printerOrientation">>
  ) => void
}

const EditTemplateDimensionsModal = ({
  isOpen,
  onClose,
  template,
  onTemplateChange,
}: Props) => {
  // Get displayUnit from context if available, fallback to "mm"
  const uiContext = useContext(BadgeUIContext)
  const displayUnit = uiContext?.displayUnit || "mm"

  const [customWidth, setCustomWidth] = useState<number>(template.widthMm)
  const [customHeight, setCustomHeight] = useState<number>(template.heightMm)

  // Determine initial orientation based on which dimension equals the fixed side
  const capability = getCapability(template.printerModel, template.printerMedia)
  const { printableSize } = capability
  const isRollMedia = printableSize.type === "roll"
  const getInitialOrientation = () => {
    if (!isRollMedia) {
      return undefined
    }

    if (Math.abs(template.widthMm - printableSize.widthMm) < 0.1) {
      return "perpendicular" as const // Width is fixed
    }

    return "parallel" as const // Height is fixed
  }

  const [orientation, setOrientation] = useState<("parallel" | "perpendicular") | undefined>(getInitialOrientation())

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setCustomWidth(template.widthMm)
      setCustomHeight(template.heightMm)
      setOrientation(getInitialOrientation())
    }
  }, [isOpen, template])

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      (e.currentTarget as HTMLInputElement).blur()
    }
  }

  const handleSave = () => {
    // Create a temporary template for validation
    const tempTemplate: BadgeTemplate = {
      ...template,
      widthMm: customWidth,
      heightMm: customHeight,
    }

    const validation = validateTemplateDimensions(tempTemplate)

    if (!validation.valid) {
      errorToast({
        title: "Invalid Dimensions",
        description: validation.error,
      })

      return
    }

    // Calculate new print instructions
    const newPrintInstructions = calculatePrintInstructions({
      widthMm: customWidth,
      heightMm: customHeight,
      printerModel: template.printerModel,
      printerMedia: template.printerMedia,
    })

    // Update the template properties
    onTemplateChange({
      widthMm: customWidth,
      heightMm: customHeight,
      printerOrientation: newPrintInstructions,
    })

    // Show success toast with dimensions in current unit
    successToast({
      title: "Dimensions updated",
      description: `Template dimensions have been set to ${formatMeasurement(customWidth, displayUnit)} × ${formatMeasurement(customHeight, displayUnit)}`,
    })

    onClose()
  }

  const handleCancel = () => {
    // Reset to original values
    setCustomWidth(template.widthMm)
    setCustomHeight(template.heightMm)

    onClose()
  }

  // Check if anything has changed
  const hasChanges = customWidth !== template.widthMm
    || customHeight !== template.heightMm

  // Calculate current print instructions for debugging
  const currentPrintInstructions = calculatePrintInstructions({
    widthMm: customWidth,
    heightMm: customHeight,
    printerModel: template.printerModel,
    printerMedia: template.printerMedia,
  })

  const actions = [
    <Button variant="outline" colorScheme="button.light" onClick={handleCancel}>
      Cancel
    </Button>,
    <Button
      onClick={handleSave}
      isDisabled={!hasChanges}
    >
      Save
    </Button>,
  ]

  return (
    <Modal
      show={isOpen}
      hide={handleCancel}
      title="Badge Dimensions"
      size="xl"
      actions={actions}
    >
      <VStack spacing={6} align="stretch">
        <BadgeDimensionsForm
          template={{
            ...template,
            widthMm: customWidth,
            heightMm: customHeight,
          }}
          onWidthChange={setCustomWidth}
          onHeightChange={setCustomHeight}
          onInputKeyDown={handleInputKeyDown}
          orientation={orientation}
          onOrientationChange={(newOrientation) => setOrientation(newOrientation)}
        />

        <DevOnly>
          <Box p={4} bg="purple.50" borderRadius="md" borderWidth={1} borderColor="purple.200">
            <Text fontSize="sm" fontWeight="semibold" mb={2} color="purple.700">
              Dev Mode Helper: Print Instructions
            </Text>
            <VStack align="stretch" spacing={2}>
              <HStack>
                <Text fontSize="sm" color="gray.600" minW="120px">printOrientation</Text>
                <Badge
                  colorScheme={
                  currentPrintInstructions === "portrait"
                    ? "purple"
                    : currentPrintInstructions === "landscape"
                      ? "blue"
                      : "gray"
                }
                  fontSize="sm"
                >
                  {currentPrintInstructions}
                </Badge>
              </HStack>
              <Box pt={2}>
                <Text fontSize="xs" color="purple.600">
                  {printableSize.type === "roll" ? (
                    Math.abs(customWidth - printableSize.widthMm) < 0.1
                    && Math.abs(customHeight - printableSize.widthMm) < 0.1
                      ? `Square badge (${printableSize.widthMm}mm × ${printableSize.widthMm}mm) - orientation doesn't matter`
                      : Math.abs(customWidth - printableSize.widthMm) < 0.1
                        ? `Because the printer media's fixed side (${printableSize.widthMm}mm) is the badge's width`
                        : Math.abs(customHeight - printableSize.widthMm) < 0.1
                          ? `Because the printer media's fixed side (${printableSize.widthMm}mm) is the badge's height`
                          : "Invalid: One dimension must equal the fixed side"
                  ) : printableSize.type === "fixed"
                    ? `This media has fixed dimensions of ${printableSize.widthMm}mm × ${printableSize.heightMm}mm.`
                    : "Generic media allows custom width and height within configured limits."}
                </Text>
              </Box>
            </VStack>
          </Box>
        </DevOnly>
      </VStack>
    </Modal>
  )
}

export default EditTemplateDimensionsModal
